/**
 * OnboardingGuide — Contextual Tooltip System (Intercom-style)
 *
 * A floating bot avatar that launches guided tours through the app.
 * Features:
 *  – Floating action button with bot avatar (always visible)
 *  – Pop-up menu to start/choose tours
 *  – Full-screen overlay with spotlight on target elements
 *  – Positioned tooltips with navigation (prev/next/close)
 *  – Auto-detects current page tour
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { findTourForPath, getPageTours, type GuideStep, type PageTour } from './onboardingGuideConfig';
import { useThemeLanguage } from '@/contexts/ThemeLanguageContext';
import guideBotAvatar from '@/assets/saity.png';
import './OnboardingGuide.css';

// ────────────────────────────────────────────────
//  Types
// ────────────────────────────────────────────────
interface TooltipPosition {
    top: number;
    left: number;
}

interface SpotlightRect {
    top: number;
    left: number;
    width: number;
    height: number;
}

// ────────────────────────────────────────────────
//  Helper: compute tooltip position relative to element
// ────────────────────────────────────────────────
function computeTooltipPosition(
    elRect: DOMRect,
    tooltipWidth: number,
    tooltipHeight: number,
    preferredPosition: GuideStep['position'],
    gap: number = 16
): { pos: TooltipPosition; actualPosition: GuideStep['position'] } {
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const positions: Record<GuideStep['position'], TooltipPosition> = {
        top: {
            top: elRect.top - tooltipHeight - gap,
            left: elRect.left + elRect.width / 2 - tooltipWidth / 2,
        },
        bottom: {
            top: elRect.bottom + gap,
            left: elRect.left + elRect.width / 2 - tooltipWidth / 2,
        },
        left: {
            top: elRect.top + elRect.height / 2 - tooltipHeight / 2,
            left: elRect.left - tooltipWidth - gap,
        },
        right: {
            top: elRect.top + elRect.height / 2 - tooltipHeight / 2,
            left: elRect.right + gap,
        },
    };

    // Try preferred position first, then fallback in order
    const fallbackOrder: GuideStep['position'][] = ['bottom', 'top', 'right', 'left'];
    const tryOrder = [preferredPosition, ...fallbackOrder.filter((p) => p !== preferredPosition)];

    for (const attempt of tryOrder) {
        const p = positions[attempt];
        const fitsX = p.left >= 8 && p.left + tooltipWidth <= vw - 8;
        const fitsY = p.top >= 8 && p.top + tooltipHeight <= vh - 8;
        if (fitsX && fitsY) {
            return { pos: p, actualPosition: attempt };
        }
    }

    // If nothing fits perfectly, use bottom and clamp
    const pos = { ...positions.bottom };
    pos.left = Math.max(8, Math.min(pos.left, vw - tooltipWidth - 8));
    pos.top = Math.max(8, Math.min(pos.top, vh - tooltipHeight - 8));
    return { pos, actualPosition: 'bottom' };
}

// ────────────────────────────────────────────────
//  Helper: find first matching element from split selectors
// ────────────────────────────────────────────────
function findElement(selectorString: string): Element | null {
    const selectors = selectorString.split(',').map((s) => s.trim());
    for (const sel of selectors) {
        try {
            const el = document.querySelector(sel);
            if (el) return el;
        } catch {
            // Invalid selector, skip
        }
    }
    return null;
}

// ────────────────────────────────────────────────
//  Component
// ────────────────────────────────────────────────
export function OnboardingGuide() {
    const location = useLocation();
    const { lang } = useThemeLanguage();
    const isEn = lang === 'en';

    // State
    const [menuOpen, setMenuOpen] = useState(false);
    const [menuExiting, setMenuExiting] = useState(false);
    const [activeTour, setActiveTour] = useState<PageTour | null>(null);
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
    const [tooltipPos, setTooltipPos] = useState<TooltipPosition>({ top: 0, left: 0 });
    const [tooltipActualPos, setTooltipActualPos] = useState<GuideStep['position']>('bottom');
    const [tooltipVisible, setTooltipVisible] = useState(false);
    const [tooltipExiting, setTooltipExiting] = useState(false);

    const tooltipRef = useRef<HTMLDivElement>(null);
    const animFrameRef = useRef<number>(0);

    // Current tour for this page
    const currentPageTour = findTourForPath(location.pathname, lang);

    // Update active tour translation when language changes
    const [prevLang, setPrevLang] = useState(lang);
    useEffect(() => {
        if (lang !== prevLang) {
            setPrevLang(lang);
            if (activeTour) {
                const translatedTour = getPageTours(lang).find(t => t.id === activeTour.id);
                if (translatedTour) {
                    setActiveTour(translatedTour);
                }
            }
        }
    }, [lang, prevLang, activeTour]);

    // ────────── Menu toggle ──────────
    const openMenu = useCallback(() => {
        setMenuOpen(true);
        setMenuExiting(false);
    }, []);

    const closeMenu = useCallback(() => {
        setMenuExiting(true);
        setTimeout(() => {
            setMenuOpen(false);
            setMenuExiting(false);
        }, 250);
    }, []);

    const toggleMenu = useCallback(() => {
        if (activeTour) return; // Don't toggle if tour is active
        if (menuOpen) closeMenu();
        else openMenu();
    }, [menuOpen, activeTour, openMenu, closeMenu]);

    // ────────── Position tooltip & spotlight ──────────
    const positionStep = useCallback(
        (step: GuideStep) => {
            const el = findElement(step.selector);
            if (!el) {
                // Element not found, skip visually but keep tooltip near center
                setSpotlight(null);
                setTooltipPos({ top: window.innerHeight / 2 - 100, left: window.innerWidth / 2 - 170 });
                setTooltipActualPos('bottom');
                setTooltipVisible(true);
                return;
            }

            const rect = el.getBoundingClientRect();
            const pad = step.highlightPadding ?? 8;

            // Scroll element into view if needed
            const isVisible =
                rect.top >= 0 && rect.bottom <= window.innerHeight && rect.left >= 0 && rect.right <= window.innerWidth;

            if (!isVisible) {
                el.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                // Wait for scroll to finish, then reposition
                setTimeout(() => positionStep(step), 400);
                return;
            }

            // Set spotlight (relative to viewport)
            setSpotlight({
                top: Math.round(Math.max(0, rect.top - pad)),
                left: Math.round(Math.max(0, rect.left - pad)),
                width: Math.round(Math.min(window.innerWidth - (rect.left - pad), rect.width + pad * 2)),
                height: Math.round(Math.min(window.innerHeight - (rect.top - pad), rect.height + pad * 2)),
            });

            // Compute tooltip position
            const tooltipWidth = 340;
            const tooltipHeight = 220; // Approximate
            const { pos, actualPosition } = computeTooltipPosition(rect, tooltipWidth, tooltipHeight, step.position);
            setTooltipPos(pos);
            setTooltipActualPos(actualPosition);
            setTooltipVisible(true);
        },
        []
    );

    // ────────── Start tour ──────────
    const startTour = useCallback(
        (tour: PageTour) => {
            setMenuOpen(false);
            setMenuExiting(false);
            setActiveTour(tour);
            setCurrentStepIndex(0);
            setTooltipExiting(false);
            // Position first step after a small delay (DOM ready)
            setTimeout(() => {
                positionStep(tour.steps[0]);
            }, 100);
        },
        [positionStep]
    );

    // ────────── Navigate steps ──────────
    const goToStep = useCallback(
        (index: number) => {
            if (!activeTour) return;
            if (index < 0 || index >= activeTour.steps.length) return;

            setTooltipExiting(true);
            setTimeout(() => {
                setTooltipExiting(false);
                setCurrentStepIndex(index);
                positionStep(activeTour.steps[index]);
            }, 200);
        },
        [activeTour, positionStep]
    );

    const nextStep = useCallback(() => {
        if (!activeTour) return;
        if (currentStepIndex < activeTour.steps.length - 1) {
            goToStep(currentStepIndex + 1);
        } else {
            closeTour();
        }
    }, [activeTour, currentStepIndex, goToStep]);

    const prevStep = useCallback(() => {
        goToStep(currentStepIndex - 1);
    }, [currentStepIndex, goToStep]);

    // ────────── Close tour ──────────
    const closeTour = useCallback(() => {
        setTooltipExiting(true);
        setTimeout(() => {
            setActiveTour(null);
            setCurrentStepIndex(0);
            setSpotlight(null);
            setTooltipVisible(false);
            setTooltipExiting(false);
        }, 200);
    }, []);

    // ────────── Handle window resize ──────────
    useEffect(() => {
        if (!activeTour) return;

        const handleResize = () => {
            cancelAnimationFrame(animFrameRef.current);
            animFrameRef.current = requestAnimationFrame(() => {
                positionStep(activeTour.steps[currentStepIndex]);
            });
        };

        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize, true);

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize, true);
            cancelAnimationFrame(animFrameRef.current);
        };
    }, [activeTour, currentStepIndex, positionStep]);

    // Close tour on route change
    useEffect(() => {
        if (activeTour) {
            closeTour();
        }
    }, [location.pathname]);

    // ────────── Keyboard navigation ──────────
    useEffect(() => {
        if (!activeTour) return;

        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') closeTour();
            if (e.key === 'ArrowRight' || e.key === 'Enter') nextStep();
            if (e.key === 'ArrowLeft') prevStep();
        };

        window.addEventListener('keydown', handleKey);
        return () => window.removeEventListener('keydown', handleKey);
    }, [activeTour, nextStep, prevStep, closeTour]);

    // ────────── Current step data ──────────
    const currentStep = activeTour ? activeTour.steps[currentStepIndex] : null;
    const totalSteps = activeTour ? activeTour.steps.length : 0;
    const isLastStep = activeTour ? currentStepIndex === activeTour.steps.length - 1 : false;

    // ────────── Render ──────────
    return React.createElement(
        React.Fragment,
        null,

        // === Floating Action Button ===
        !activeTour &&
        React.createElement(
            'button',
            {
                type: 'button',
                className: `onboarding-fab ${!menuOpen ? 'onboarding-fab--pulse' : ''}`,
                onClick: toggleMenu,
                'aria-label': isEn ? 'Help guide' : 'Guía de ayuda',
                id: 'onboarding-guide-fab',
            },
            React.createElement('img', {
                src: guideBotAvatar,
                alt: isEn ? 'Guide assistant' : 'Asistente de guía',
                className: 'onboarding-fab__avatar',
            })
        ),

        // === Pop-up Menu ===
        menuOpen &&
        !activeTour &&
        React.createElement(
            'div',
            {
                className: `onboarding-fab-menu ${menuExiting ? 'onboarding-fab-menu--exit' : ''}`,
                id: 'onboarding-guide-menu',
            },
            // Close button
            React.createElement(
                'button',
                {
                    type: 'button',
                    className: 'onboarding-fab-menu__close',
                    onClick: closeMenu,
                    'aria-label': isEn ? 'Close menu' : 'Cerrar menú',
                },
                '✕'
            ),

            // Header
            React.createElement(
                'div',
                { className: 'onboarding-fab-menu__header' },
                React.createElement('div', { className: 'onboarding-mascot-floating-container' },
                    React.createElement('img', {
                        src: guideBotAvatar,
                        alt: '',
                        className: 'onboarding-mascot-img',
                    })
                ),
                React.createElement(
                    'div',
                    { className: 'onboarding-fab-menu__greeting' },
                    React.createElement('h3', null, isEn ? 'Hi! I am Saity' : '¡Hola! Soy Saity'),
                    React.createElement('p', null, isEn ? 'Need help? I can guide you through the entire AI Recruiter platform.' : '¿Necesitas ayuda? Te puedo guiar por toda la plataforma de AI Recruiter.')
                )
            ),

            // Start current page tour button
            currentPageTour &&
            React.createElement(
                'div',
                { className: 'onboarding-fab-menu__body' },
                React.createElement(
                    'button',
                    {
                        type: 'button',
                        className: 'onboarding-fab-menu__start-btn',
                        onClick: () => startTour(currentPageTour),
                    },
                    (isEn ? 'Start guide: ' : 'Iniciar guía: ') + currentPageTour.name
                )
            ),

            // Only show the general dashboard tour if we are at the dashboard and not already suggesting it
            !currentPageTour && location.pathname.startsWith('/recruiter') &&
            React.createElement(
                'div',
                { className: 'onboarding-fab-menu__tours' },
                React.createElement(
                    'button',
                    {
                        type: 'button',
                        className: 'onboarding-fab-menu__tour-btn',
                        onClick: () => {
                            const dashboardTour = findTourForPath('/recruiter', lang);
                            if (dashboardTour) startTour(dashboardTour);
                        },
                    },
                    isEn ? 'Tour: Main Dashboard' : 'Tour: Panel Principal'
                )
            )
        ),

        // === Tour Active Overlay ===
        activeTour &&
        React.createElement(
            'div',
            { className: 'onboarding-overlay', id: 'onboarding-overlay' },

            // Spotlight
            spotlight &&
            React.createElement('div', {
                className: 'onboarding-spotlight',
                style: {
                    top: spotlight.top,
                    left: spotlight.left,
                    width: spotlight.width,
                    height: spotlight.height,
                },
            }),

            // Backdrop (click to close)
            React.createElement('div', {
                className: 'onboarding-overlay__backdrop',
                onClick: closeTour,
                style: spotlight
                    ? {
                        // Use clip-path to cut out the spotlight area from the backdrop
                        clipPath: `polygon(
                  0% 0%, 100% 0%, 100% 100%, 0% 100%,
                  0% ${spotlight.top}px,
                  ${spotlight.left}px ${spotlight.top}px,
                  ${spotlight.left}px ${spotlight.top + spotlight.height}px,
                  ${spotlight.left + spotlight.width}px ${spotlight.top + spotlight.height}px,
                  ${spotlight.left + spotlight.width}px ${spotlight.top}px,
                  0% ${spotlight.top}px
                )`,
                    }
                    : undefined,
            }),

            // Tooltip
            tooltipVisible &&
            currentStep &&
            React.createElement(
                'div',
                {
                    ref: tooltipRef,
                    className: `onboarding-tooltip onboarding-tooltip--${tooltipActualPos} ${tooltipExiting ? 'onboarding-tooltip--exit' : ''}`,
                    style: { top: tooltipPos.top, left: tooltipPos.left },
                    id: 'onboarding-tooltip',
                },
                // Arrow
                React.createElement('div', { className: 'onboarding-tooltip__arrow' }),

                // Close
                React.createElement(
                    'button',
                    {
                        type: 'button',
                        className: 'onboarding-tooltip__close',
                        onClick: closeTour,
                        'aria-label': isEn ? 'Close guide' : 'Cerrar guía',
                    },
                    '✕'
                ),

                // Header
                React.createElement(
                    'div',
                    { className: 'onboarding-tooltip__header' },
                    React.createElement('div', { className: 'onboarding-mascot-floating-container' },
                        React.createElement('img', {
                            src: guideBotAvatar,
                            alt: 'Saity mascot',
                            className: 'onboarding-mascot-img',
                        })
                    ),
                    React.createElement('h4', { className: 'onboarding-tooltip__title' }, currentStep.title)
                ),

                // Body
                React.createElement(
                    'div',
                    { className: 'onboarding-tooltip__body' },
                    React.createElement('p', { className: 'onboarding-tooltip__description' }, currentStep.description)
                ),

                // Footer
                React.createElement(
                    'div',
                    { className: 'onboarding-tooltip__footer' },
                    // Progress
                    React.createElement(
                        'div',
                        { className: 'onboarding-tooltip__progress' },
                        React.createElement('span', null, `${currentStepIndex + 1} ${isEn ? 'of' : 'de'} ${totalSteps}`),
                        React.createElement(
                            'div',
                            { className: 'onboarding-tooltip__progress-bar' },
                            Array.from({ length: totalSteps }, (_, i) =>
                                React.createElement('div', {
                                    key: i,
                                    className: `onboarding-tooltip__progress-dot ${i === currentStepIndex
                                        ? 'onboarding-tooltip__progress-dot--active'
                                        : i < currentStepIndex
                                            ? 'onboarding-tooltip__progress-dot--completed'
                                            : ''
                                        }`,
                                })
                            )
                        )
                    ),

                    // Navigation
                    React.createElement(
                        'div',
                        { className: 'onboarding-tooltip__nav' },
                        // Previous button
                        React.createElement(
                            'button',
                            {
                                type: 'button',
                                className: 'onboarding-tooltip__nav-btn onboarding-tooltip__nav-btn--prev',
                                onClick: prevStep,
                                disabled: currentStepIndex === 0,
                            },
                            isEn ? 'Previous' : 'Anterior'
                        ),
                        // Next / Finish button
                        isLastStep
                            ? React.createElement(
                                'button',
                                {
                                    type: 'button',
                                    className: 'onboarding-tooltip__nav-btn onboarding-tooltip__nav-btn--finish',
                                    onClick: closeTour,
                                },
                                isEn ? 'Done!' : '¡Listo!'
                            )
                            : React.createElement(
                                'button',
                                {
                                    type: 'button',
                                    className: 'onboarding-tooltip__nav-btn onboarding-tooltip__nav-btn--next',
                                    onClick: nextStep,
                                },
                                isEn ? 'Next' : 'Siguiente'
                            )
                    )
                )
            )
        )
    );
}
